/* Pilatoes motion — staggered scroll reveal.
   Savor ships page transitions, card hover effects and fly-to-cart, but nothing
   that brings a section's contents in as you reach them. This adds that.

   No section file is touched. The script finds each section's own layout row and
   staggers its children, so it keeps working when blocks are added or reordered
   in the editor.

   Nothing is hidden until this script runs, so a JS failure leaves the page
   fully visible. Honours prefers-reduced-motion. */
(() => {
  const cfg = window.PilatoesMotion || {};
  if (!cfg.enabled) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
  if (!('IntersectionObserver' in window)) return;

  const SKIP = new Set(['SCRIPT', 'STYLE', 'LINK', 'TEMPLATE', 'NOSCRIPT', 'BR']);
  const MAX_PER_GROUP = 14;
  const MAX_DEPTH = 8;

  const usable = (el) =>
    !SKIP.has(el.tagName) &&
    !el.hasAttribute('data-plt-no-reveal') &&
    !(el.offsetWidth === 0 && el.offsetHeight === 0);

  /* Walk past single-child wrappers to the row that actually holds the content,
     so the stagger lands on real items rather than one outer div. */
  const groupFor = (section) => {
    let node = section;
    for (let depth = 0; depth < MAX_DEPTH; depth++) {
      const kids = Array.from(node.children).filter(usable);
      if (kids.length === 0) return [];
      if (kids.length === 1) { node = kids[0]; continue; }
      return kids.slice(0, MAX_PER_GROUP);
    }
    return Array.from(node.children).filter(usable).slice(0, MAX_PER_GROUP);
  };

  const reveal = (el) => {
    el.classList.add('plt-in');
    /* Drop the hooks once played so hover/transform effects inside are not
       fighting a lingering transition. */
    window.setTimeout(() => {
      el.removeAttribute('data-plt-reveal');
      el.style.removeProperty('--plt-i');
      el.classList.remove('plt-in');
    }, (cfg.duration || 620) + (cfg.stagger || 90) * MAX_PER_GROUP + 200);
  };

  const observer = new IntersectionObserver((entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      reveal(entry.target);
      observer.unobserve(entry.target);
    }
  }, { rootMargin: '0px 0px -6% 0px', threshold: 0.01 });

  const prepare = (scope) => {
    const sections = Array.from(
      (scope || document).querySelectorAll('#MainContent .shopify-section')
    ).filter((s) => !s.hasAttribute('data-plt-no-reveal'));

    sections.forEach((section, sectionIndex) => {
      if (section.dataset.pltPrepared) return;
      section.dataset.pltPrepared = '1';

      const group = groupFor(section);
      if (group.length < 2) return;

      group.forEach((el, i) => {
        el.setAttribute('data-plt-reveal', '');
        el.style.setProperty('--plt-i', String(i));
      });

      /* The first section is already on screen at load: play it straight away as
         an entrance rather than making the visitor scroll to trigger it. */
      if (sectionIndex === 0) {
        requestAnimationFrame(() => requestAnimationFrame(() => {
          group.forEach(reveal);
        }));
        return;
      }

      group.forEach((el) => observer.observe(el));
    });
  };

  document.documentElement.classList.add('plt-motion');

  const start = () => prepare(document);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  /* Sections re-render in the theme editor and on section reloads. */
  document.addEventListener('shopify:section:load', (event) => {
    const target = event.target;
    if (target && target.removeAttribute) target.removeAttribute('data-plt-prepared');
    prepare(document);
  });
})();
